import { CreateQuoteDialog } from "@/components/quotes/CreateQuoteDialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { GetOptions, QueryConstraint } from "@/core";
import { Deal } from "@/core/entities/deal";
import { useDeals, useGetDealById } from "@/hooks/repository-hooks/deal/use-deal";
import { useGetEventsByDealId } from "@/hooks/repository-hooks/event/use-event";
import {
  createAllFilterHandler,
  createStageFilterHandler,
  getAllStages,
  getOrderByField,
  getStageDisplayName,
  getStageValue,
  transformDealValueToName,
} from "@/utils";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  ArrowDownFromLine,
  ArrowUpFromLine,
  Columns,
  Eye,
  Filter,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DealDialog } from "./DealDialog";
// import { AuditLogDialog } from "@/components/audit-log/AuditLogDialog";

// Helper function to get the appropriate icon for business lost reason
const getBusinessLostReasonIcon = (reason: string) => {
  const lowerReason = reason.toLowerCase();

  if (lowerReason.includes("less") || lowerReason.includes("below minimum")) {
    return <ArrowDownFromLine className="h-5 w-5 text-red-500 cursor-help" />;
  }
  if (
    lowerReason.includes("maximum") ||
    lowerReason.includes("above maximum")
  ) {
    return <ArrowUpFromLine className="h-5 w-5 text-red-500 cursor-help" />;
  }
  if (
    lowerReason.includes("product not found") ||
    lowerReason.includes("not found")
  ) {
    return <X className="h-5 w-5 text-red-500 cursor-help" />;
  }
  if (
    lowerReason.includes("not enable") ||
    lowerReason.includes("not enabled") ||
    lowerReason.includes("disabled")
  ) {
    return <AlertCircle className="h-5 w-5 text-red-500 cursor-help" />;
  }

  // Default fallback icon
  return <AlertTriangle className="h-5 w-5 text-red-500 cursor-help" />;
};

export default function DealsTable() {
  // const [auditLogOpen, setAuditLogOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreateQuoteOpen, setIsCreateQuoteOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const pageSize = 10;
  const selectedDealId = searchParams.get("dealId") || "";
  const dealDialogOpen = selectedDealId.length > 0;
  const { data: selectedDeal } = useGetDealById(selectedDealId);

  // Create filter handlers with search reset functionality
  const handleStageFilter = createStageFilterHandler(
    setStageFilter,
    setTypeFilter,
    setSearchTerm,
  );
  const handleAllFilter = createAllFilterHandler(
    setStageFilter,
    setTypeFilter,
    setSearchTerm,
  );
  const allStages = getAllStages();

  // Type filter handler with search reset
  const handleTypeFilter = (type: string) => {
    setTypeFilter(type);
    setStageFilter("all");
    setSearchTerm("");
  };

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    product: true,
    type: true,
    stage: true,
    statusTier: true,
    businessLostReason: false,
    enteredPending: true,
    amount: true,
    quantity: true,
    createdAt: true,
    actions: true,
  });

  const queryOptions: GetOptions = useMemo(() => {
    const constraints: QueryConstraint[] = [];

    if (typeFilter !== "all") {
      constraints.push({ field: "type", operator: "==", value: typeFilter });
    }

    if (stageFilter !== "all") {
      constraints.push({
        field: "stage",
        operator: "==",
        value: Number(getStageValue(stageFilter)),
      });
    }

    if (searchTerm.trim()) {
      // Use startAt/endAt pattern for efficient prefix search on deal name
      const searchText = searchTerm.trim();
      constraints.push(
        { field: "name", operator: ">=", value: searchText },
        { field: "name", operator: "<=", value: searchText + "\uf8ff" },
      );
    }

    return {
      queryConstraints: constraints,
      pagination: {
        limit: pageSize,
      },
      orderBy: {
        field: searchTerm.trim()
          ? "name"
          : getOrderByField(typeFilter, stageFilter),
        direction: "desc",
      },
    };
  }, [pageSize, typeFilter, stageFilter, searchTerm]);

  const dealsData = useDeals(queryOptions);
  const eventsData = useGetEventsByDealId(
    {
      pagination: {
        limit: 20,
      },
    },
    selectedDealId,
  );

  const deals = useMemo(() => {
    if (!dealsData.data) {
      return [];
    }

    const allDeals = dealsData.data.pages.flat();

    // Additional client-side filtering for deal name and product name when searching
    let filteredDeals = allDeals;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredDeals = allDeals.filter(
        (deal) =>
          deal.name.toLowerCase().includes(searchLower) ||
          deal.productName.toLowerCase().includes(searchLower),
      );
    }

    // Sort appropriately
    return searchTerm.trim()
      ? filteredDeals // Keep Firestore ordering when searching
      : filteredDeals.sort(
          (a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
        );
  }, [dealsData.data, searchTerm]);

  const events = useMemo(() => {
    if (eventsData.isLoading || !eventsData.data) {
      return [];
    }
    return eventsData.data.pages
      .flatMap((page) => page)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [eventsData.data, eventsData.isLoading]);

  const queryClient = useQueryClient();

  const handleRefresh = () => {
    // Invalidate and refetch the deals query to start fresh
    queryClient.invalidateQueries({ queryKey: ["deals", "get"] });
  };

  const handleLoadMore = () => {
    if (dealsData.hasNextPage && !dealsData.isFetchingNextPage) {
      dealsData.fetchNextPage();
    }
  };

  const handleViewDeal = (deal: Deal) => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("dealId", deal.id);
    setSearchParams(nextSearchParams);
  };

  const handleDealDialogOpenChange = (open: boolean) => {
    if (open) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("dealId");
    setSearchParams(nextSearchParams);
  };

  // const handleViewAuditLog = (deal: Deal) => {
  //   setSelectedDeal(deal);
  //   setAuditLogOpen(true);
  // };

  if (dealsData.error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-destructive">
          Error loading deals: {dealsData.error.message}
        </div>
      </div>
    );
  }

  const renderDealsContent = () => {
    if (dealsData.isLoading) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">Loading deals...</div>
        </div>
      );
    }

    if (!deals || deals.length === 0) {
      return (
        <div className="flex items-center justify-center p-8">
          <div className="text-muted-foreground">No deals found</div>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow className="border-b">
            {columnVisibility.name && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">Name</div>
              </TableHead>
            )}
            {columnVisibility.product && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">Product</div>
              </TableHead>
            )}
            {columnVisibility.type && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">Type</div>
              </TableHead>
            )}
            {columnVisibility.stage && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">Stage</div>
              </TableHead>
            )}
            {columnVisibility.statusTier && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">
                  Status Tier
                </div>
              </TableHead>
            )}
            {columnVisibility.businessLostReason && (
              <TableHead className="relative group whitespace-nowrap text-center border-r last:border-r-0 px-2">
                <div className="flex items-center justify-center">
                  Business Lost Reason
                </div>
              </TableHead>
            )}
            {columnVisibility.enteredPending && (
              <TableHead className="relative group whitespace-nowrap text-center border-r last:border-r-0 px-2">
                <div className="flex items-center justify-center">
                  Entered Pending
                </div>
              </TableHead>
            )}
            {columnVisibility.amount && (
              <TableHead className="relative group whitespace-nowrap text-right border-r last:border-r-0 px-2">
                <div className="flex items-center justify-end">Amount</div>
              </TableHead>
            )}
            {columnVisibility.quantity && (
              <TableHead className="relative group whitespace-nowrap text-right border-r last:border-r-0 px-2">
                <div className="flex items-center justify-end">Quantity</div>
              </TableHead>
            )}
            {columnVisibility.createdAt && (
              <TableHead className="relative group whitespace-nowrap text-left border-r last:border-r-0 px-2">
                <div className="flex items-center justify-start">
                  Created At
                </div>
              </TableHead>
            )}
            {columnVisibility.actions && (
              <TableHead className="relative group whitespace-nowrap text-center border-r last:border-r-0 w-12">
                <div className="flex items-center justify-center"></div>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {deals.map((deal) => (
            <TableRow key={deal.id} className="hover:bg-muted/50 border-b">
              {columnVisibility.name && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 font-medium">
                  {deal.name}
                </TableCell>
              )}
              {columnVisibility.product && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                  {deal.productName}
                </TableCell>
              )}
              {columnVisibility.type && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      deal.buyOrSell === "buy"
                        ? "bg-green-100 text-green-800"
                        : "bg-blue-100 text-blue-800"
                    }`}
                  >
                    {deal.buyOrSell}
                  </span>
                </TableCell>
              )}
              {columnVisibility.stage && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      transformDealValueToName(deal.stage.toString()) ===
                      "CLOSED_WON"
                        ? "bg-green-100 text-green-800"
                        : transformDealValueToName(deal.stage.toString()) ===
                            "CLOSED_LOST"
                          ? "bg-red-100 text-red-800"
                          : "bg-yellow-100 text-yellow-800"
                    }`}
                  >
                    {transformDealValueToName(deal.stage.toString())}
                  </span>
                </TableCell>
              )}
              {columnVisibility.statusTier && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                  {deal.statusTier ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      {deal.statusTier.name} • {deal.statusTier.percentage}%
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              )}
              {columnVisibility.businessLostReason && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 text-muted-foreground">
                  <div className="flex justify-center items-center">
                    {deal.businessLostReason ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          {getBusinessLostReasonIcon(deal.businessLostReason)}
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{deal.businessLostReason}</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </div>
                </TableCell>
              )}
              {columnVisibility.enteredPending && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      deal.enteredPending
                        ? "bg-yellow-100 text-yellow-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {deal.enteredPending ? "Yes" : "No"}
                  </span>
                </TableCell>
              )}
              {columnVisibility.amount && (
                <TableCell className="whitespace-nowrap text-right border-r last:border-r-0">
                  ${deal.amount.toLocaleString()}
                </TableCell>
              )}
              {columnVisibility.quantity && (
                <TableCell className="whitespace-nowrap text-right border-r last:border-r-0">
                  {deal.quantity.toLocaleString()}
                </TableCell>
              )}
              {columnVisibility.createdAt && (
                <TableCell className="whitespace-nowrap text-left border-r last:border-r-0 text-muted-foreground">
                  {format(new Date(deal.createdAt), "MMM dd, yyyy")}
                </TableCell>
              )}
              {columnVisibility.actions && (
                <TableCell className="whitespace-nowrap text-center border-r last:border-r-0 px-0 w-12">
                  <div className="flex items-center justify-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => handleViewDeal(deal)}>
                          <Eye className="mr-2 h-4 w-4" />
                          View Deal Event
                        </DropdownMenuItem>
                        {/* <DropdownMenuItem onClick={() => handleViewAuditLog(deal)}>
                          <FileText className="mr-2 h-4 w-4" />
                          View Audit Log
                        </DropdownMenuItem> */}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <div className="space-y-4">
      {/* Search and Actions Bar */}
      <div className="flex items-center">
        <h2 className="text-2xl font-bold mb-4">Deals</h2>
        <h3 className="text-lg ml-2 text-muted-foreground self-start mt-1">
          ({deals?.length || 0})
        </h3>
        <Button
          size="sm"
          className="ml-auto mb-4"
          onClick={() => setIsCreateQuoteOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Quote
        </Button>
      </div>
      <CreateQuoteDialog
        open={isCreateQuoteOpen}
        onOpenChange={setIsCreateQuoteOpen}
      />

      {/* Search Input */}
      <div className="flex items-center justify-between">
        <div className="relative flex-1 max-w-sm items-center">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search deals..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setStageFilter("all");
              setTypeFilter("all");
            }}
            className="pl-10"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          className="py-4"
          disabled={dealsData.isLoading || dealsData.isFetching}
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${dealsData.isFetching ? "animate-spin" : ""}`}
          />
          Refresh
        </Button>
      </div>
      <div className="flex items-center justify-between h-[34px]">
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="py-4">
                <Filter className="h-4 w-4 mr-2" />
                Type: {typeFilter === "all" ? "All" : typeFilter}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleTypeFilter("all")}>
                All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTypeFilter("Purchase")}>
                Purchase
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleTypeFilter("Sales")}>
                Buy
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="ml-2 py-4">
                <Filter className="h-4 w-4 mr-2" />
                Stage:{" "}
                {stageFilter === "all"
                  ? "All"
                  : getStageDisplayName(stageFilter)}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Filter by Stage</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleAllFilter}>All</DropdownMenuItem>
              {allStages.map((stage) => (
                <DropdownMenuItem
                  key={stage.key}
                  onClick={() => handleStageFilter(stage.key)}
                >
                  {stage.displayName}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="py-4">
                <Columns className="mr-2 h-4 w-4" />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem
                checked={columnVisibility.name}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, name: checked }))
                }
              >
                Name
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.product}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, product: checked }))
                }
              >
                Product
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.type}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, type: checked }))
                }
              >
                Type
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.stage}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, stage: checked }))
                }
              >
                Stage
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.statusTier}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, statusTier: checked }))
                }
              >
                Status Tier
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.businessLostReason}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    businessLostReason: checked,
                  }))
                }
              >
                Business Lost Reason
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.enteredPending}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    enteredPending: checked,
                  }))
                }
              >
                Entered Pending
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.amount}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, amount: checked }))
                }
              >
                Amount
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.quantity}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    quantity: checked,
                  }))
                }
              >
                Quantity
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.createdAt}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    createdAt: checked,
                  }))
                }
              >
                Created At
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={columnVisibility.actions}
                onCheckedChange={(checked) =>
                  setColumnVisibility((prev) => ({ ...prev, actions: checked }))
                }
              >
                Actions
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-hidden overflow-x-auto">
        {renderDealsContent()}
      </div>

      {/* Load More Button */}
      {dealsData.hasNextPage && (
        <div className="flex justify-center pt-4">
          <Button
            variant="outline"
            onClick={handleLoadMore}
            disabled={dealsData.isFetchingNextPage}
          >
            {dealsData.isFetchingNextPage ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading more...
              </>
            ) : (
              "Load More"
            )}
          </Button>
        </div>
      )}

      {/* Deal Dialog */}
      <DealDialog
        open={dealDialogOpen}
        onOpenChange={handleDealDialogOpenChange}
        deal={selectedDeal || null}
        events={events}
        isLoadingEvent={Boolean(selectedDealId) && (eventsData.isLoading || !selectedDeal)}
      />

      {/* Audit Log Dialog */}
      {/* <AuditLogDialog
        open={auditLogOpen}
        onOpenChange={setAuditLogOpen}
        documentId={selectedDeal?.id || ""}
        documentName={selectedDeal?.name}
      /> */}
    </div>
  );
}
